import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { registrationApi, ageClassApi } from '../lib/api';
import { toast } from 'sonner';
import { ArrowRight, Loader2, Scale, Shuffle } from 'lucide-react';

/**
 * Lets staff change an athlete's weight from inside a championship and shows
 * a live preview of the resulting weight category before saving. On save it
 * cascades through the backend (athlete + registration + bracket).
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   championshipId: string
 *   championshipDate?: string (YYYY-MM-DD) — when provided, save is blocked
 *                                            from the event day onwards
 *   registrationId: string
 *   athleteName: string
 *   currentWeight: number
 *   currentCategory: string
 *   onSaved?: (response) => void   // called with the PATCH response payload
 */
export const EditWeightDialog = ({
  open,
  onClose,
  championshipId,
  championshipDate,
  categoryTableId,
  athleteSex,
  currentAgeClass,
  registrationId,
  athleteName,
  currentWeight,
  currentCategory,
  onSaved,
}) => {
  const [weight, setWeight] = useState('');
  const [ageClass, setAgeClass] = useState('');
  const [ageClassOptions, setAgeClassOptions] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Block edits when the championship date has already arrived/passed.
  const eventLocked = (() => {
    if (!championshipDate) return false;
    try {
      const today = new Date().toISOString().slice(0, 10);
      return String(championshipDate) <= today;
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    if (open) {
      setWeight(currentWeight ?? '');
      setAgeClass(currentAgeClass ?? '');
      setPreview(null);
    }
  }, [open, currentWeight, currentAgeClass]);

  // Load age-class options from the championship's table filtered by athlete sex
  useEffect(() => {
    if (!open || !categoryTableId) return;
    (async () => {
      try {
        const { data } = await ageClassApi.listForSex(categoryTableId, athleteSex || 'M');
        setAgeClassOptions(data.age_classes || []);
      } catch (e) {
        setAgeClassOptions([]);
      }
    })();
  }, [open, categoryTableId, athleteSex]);

  // Debounced preview
  useEffect(() => {
    if (!open || !registrationId) return undefined;
    const num = parseFloat(weight);
    if (Number.isNaN(num) || num <= 0) {
      setPreview(null);
      return undefined;
    }
    if (num === currentWeight) {
      setPreview(null);
      return undefined;
    }
    const handle = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const { data } = await registrationApi.previewWeight(championshipId, registrationId, num);
        setPreview(data);
      } catch (e) {
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [weight, currentWeight, championshipId, registrationId, open]);

  const handleSave = async () => {
    const num = parseFloat(weight);
    if (Number.isNaN(num) || num <= 0) {
      toast.error('Informe um peso válido');
      return;
    }
    const ageClassChanged = ageClass && ageClass !== currentAgeClass;
    if (num === currentWeight && !ageClassChanged) {
      toast.info('Nenhuma alteração');
      onClose?.();
      return;
    }
    setSaving(true);
    try {
      const payload = { weight: num };
      if (ageClassChanged) payload.athlete_age_class = ageClass;
      const { data } = await registrationApi.updateWeight(championshipId, registrationId, payload);
      if (ageClassChanged) {
        toast.success(`Atleta realocado para ${ageClass}`);
      } else if (data?.category_changed) {
        toast.success(`Categoria atualizada: ${data.old_category.split('|')[3]} → ${data.new_category.split('|')[3]}`);
      } else {
        toast.success('Peso atualizado');
      }
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erro ao atualizar peso';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose?.()}>
      <DialogContent className="bg-[#141414] border-[#27272A] max-w-md" data-testid="edit-weight-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-['Chivo'] flex items-center gap-2">
            <Scale className="w-5 h-5 text-[#D4AF37]" />
            Editar Peso do Atleta
          </DialogTitle>
        </DialogHeader>
        <div className="mt-2 space-y-4">
          <div className="text-[#A1A1AA] text-sm">
            Atleta: <span className="text-white font-medium">{athleteName}</span>
          </div>

          {eventLocked && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/40 p-3 text-sm text-red-300" data-testid="edit-weight-locked">
              ⚠ Alterações bloqueadas — o evento já começou ou já passou ({championshipDate}).
              Mudanças de peso só são permitidas <strong>antes do dia do campeonato</strong>.
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="weight-input" className="text-[#A1A1AA]">Novo peso (kg)</Label>
            <Input
              id="weight-input"
              type="number"
              step="0.1"
              min="20"
              max="250"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              disabled={saving || eventLocked}
              className="bg-[#1A1A1A] border-[#27272A] text-white"
              data-testid="edit-weight-input"
              autoFocus
            />
          </div>

          {/* Age-class override (internal championship / forced pairing) */}
          {ageClassOptions.length > 0 && (
            <div className="space-y-2">
              <Label className="text-[#A1A1AA] flex items-center gap-2">
                <Shuffle className="w-4 h-4 text-[#D4AF37]" />
                Classe de idade (opcional — realocar)
              </Label>
              <Select value={ageClass} onValueChange={setAgeClass} disabled={saving || eventLocked}>
                <SelectTrigger
                  data-testid="edit-age-class-select"
                  className="bg-[#1A1A1A] border-[#27272A] text-white"
                >
                  <SelectValue placeholder="Manter classe atual" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                  {ageClassOptions.map((ac) => (
                    <SelectItem key={ac} value={ac} className="text-white hover:bg-[#27272A]">
                      {ac}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {ageClass && ageClass !== currentAgeClass && (
                <p className="text-micro text-[#D4AF37]">
                  ⚠ O atleta será realocado para {ageClass} — luta casada será criada com quem estiver nessa categoria.
                </p>
              )}
            </div>
          )}

          {/* Before / After preview card */}
          <div className="rounded-lg bg-[#1A1A1A] border border-[#27272A] p-3" data-testid="edit-weight-preview">
            <div className="flex items-center gap-3 text-sm">
              <div className="flex-1">
                <div className="text-[#71717A] text-xs mb-1">Atual</div>
                <div className="text-white">
                  {currentWeight} kg <span className="text-[#A1A1AA]">— {currentCategory || 'sem categoria'}</span>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-[#D4AF37] shrink-0" />
              <div className="flex-1">
                <div className="text-[#71717A] text-xs mb-1">Após mudança</div>
                <div className="text-white">
                  {previewLoading ? (
                    <span className="text-[#71717A] flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> calculando…
                    </span>
                  ) : preview ? (
                    <>
                      {preview.preview_weight} kg{' '}
                      <span className={preview.will_change ? 'text-[#D4AF37] font-semibold' : 'text-[#A1A1AA]'}>
                        — {preview.new_category || 'sem categoria'}
                      </span>
                    </>
                  ) : (
                    <span className="text-[#71717A]">—</span>
                  )}
                </div>
              </div>
            </div>
            {preview?.will_change && (
              <div className="mt-2 text-xs text-[#D4AF37]">
                ⚠ A categoria mudará — as chaves serão regeradas automaticamente.
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => onClose?.()}
              disabled={saving}
              className="border-[#27272A] text-[#A1A1AA] hover:text-white"
              data-testid="edit-weight-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !weight || eventLocked}
              className="bg-[#D4AF37] text-black hover:bg-[#E5C158]"
              data-testid="edit-weight-save"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando…</> : 'Salvar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditWeightDialog;
